import constants from '@/shared/constants/constants';
import {
  clearRerunProjectIdSession,
  isConceptValuationRerunActive,
  resolveReuseProjectId,
  writeRerunProjectIdToSession,
} from '@/shared/helpers/conceptValuationRerun';
import { conceptValuationMenuSegmentIds } from '@/shared/mixins/conceptValuationSegmentMixin';
import {
  conceptEvaluationCreate,
  conceptEvaluationCategoryList,
  conceptEvaluationDetail,
  conceptEvaluationGenerateConceptTitles,
  conceptEvaluationCreateAnalysisReport,
  conceptEvaluationAnalysisReportDetail,
} from '@/services/project.service';
import { getAll as getPlans } from '@/services/plan.service';

function syncRootTargetCountryFromProject(commit, project) {
  const targetCountry = project && project.target_country;
  if (targetCountry != null && targetCountry !== '') {
    commit('SET_TARGET_COUNTRY', targetCountry, { root: true });
  }
}

async function ensureRootPlanAdhoc(commit, rootState) {
  if (rootState.planAdhoc) {
    return;
  }
  try {
    const { status, data } = await getPlans();
    if (status !== 200 || !Array.isArray(data)) {
      return;
    }
    const adhoc = data.find(item => item.plan_type === constants.PLAN_TYPE_ADHOC);
    if (adhoc) {
      commit('SET_PLAN_ADHOC', adhoc, { root: true });
    }
  } catch (_err) {
    // Network / getPlans() errors: adhoc price is optional.
  }
}

function newSegmentTypesForRerun(detail, draftMenus, draftSegmentTypes) {
  const menus = [...new Set((draftMenus || []).map(Number).filter(id => !Number.isNaN(id)))];
  const drafted = [...new Set(
    (draftSegmentTypes || []).map(Number).filter(id => !Number.isNaN(id)),
  )];
  if (!menus.length) {
    return drafted;
  }
  return drafted.filter(segId => menus.some((menuId) => {
    const existing = conceptValuationMenuSegmentIds(detail, menuId);
    return !existing.includes(Number(segId));
  }));
}

const SLOT_COUNT = constants.CONCEPT_VALUATION_SLOT_COUNT;

const state = {
  projectId: null,
  detail: null,
  draftProjectName: '',
  draftTargetCountry: 0,
  draftParentProjectId: null,
  draftIsAvgSegment: false,
  draftSegmentTypes: [],
  draftEvaluationMenus: [],
  categoryOptions: [],
  loading: false,
  error: null,
  /** True after returning from Result — locks segments/categories/titles behavior. */
  isRerunFromResult: false,
  lockedSegmentTypes: [],
  lockedValuationMenus: [],
  /** Incremented when draftParentProjectId changes — wizard steps reload prefilled data. */
  draftParentRevision: 0,
  /** Category selections before confirm; survives back navigation and title re-generation. */
  draftCategoryIds: Array(SLOT_COUNT).fill(null),
  /** True when SelectMenu skipped SelectSegment (Menu → InputConcepts). */
  skippedSegmentStep: false,
  analysisReport: null,
};

const mutations = {
  CV_SET_PROJECT_ID(s, id) {
    s.projectId = id;
  },
  CV_SET_DETAIL(s, payload) {
    s.detail = payload;
  },
  CV_SET_CATEGORY_OPTIONS(s, payload) {
    s.categoryOptions = payload || [];
  },
  CV_SET_DRAFT(s, payload) {
    if (payload.projectName !== undefined) s.draftProjectName = payload.projectName;
    if (payload.targetCountry !== undefined) s.draftTargetCountry = payload.targetCountry;
    if (payload.parentProjectId !== undefined) s.draftParentProjectId = payload.parentProjectId;
    if (payload.isAvgSegment !== undefined) s.draftIsAvgSegment = payload.isAvgSegment;
    if (payload.segmentTypes !== undefined) s.draftSegmentTypes = payload.segmentTypes;
    if (payload.valuationMenus !== undefined) s.draftEvaluationMenus = payload.valuationMenus;
    if (payload.skippedSegmentStep !== undefined) s.skippedSegmentStep = !!payload.skippedSegmentStep;
  },
  CV_SET_LOADING(s, v) {
    s.loading = v;
  },
  CV_SET_ERROR(s, v) {
    s.error = v;
  },
  CV_SET_RERUN_FROM_RESULT(s, payload = {}) {
    s.isRerunFromResult = true;
    s.lockedSegmentTypes = [...(payload.lockedSegmentTypes || [])];
    s.lockedValuationMenus = [...(payload.lockedValuationMenus || [])];
    s.analysisReport = null;
    if (payload.projectId != null && payload.projectId !== '') {
      s.projectId = Number(payload.projectId);
    }
  },
  CV_CLEAR_RERUN_SESSION(s) {
    s.isRerunFromResult = false;
    s.lockedSegmentTypes = [];
    s.lockedValuationMenus = [];
    s.analysisReport = null;
  },
  CV_BUMP_PARENT_REVISION(s) {
    s.draftParentRevision += 1;
  },
  CV_SET_DRAFT_CATEGORY_IDS(s, categoryIds) {
    const padded = Array.from({ length: SLOT_COUNT }, (_, i) => {
      const cid = categoryIds[i];
      return cid != null && cid !== '' ? cid : null;
    });
    s.draftCategoryIds = padded;
  },
  CV_SET_ANALYSIS_REPORT(s, payload) {
    s.analysisReport = payload || null;
  },
  CV_RESET(s) {
    s.projectId = null;
    s.detail = null;
    s.draftProjectName = '';
    s.draftTargetCountry = 0;
    s.draftParentProjectId = null;
    s.draftIsAvgSegment = false;
    s.draftSegmentTypes = [];
    s.draftEvaluationMenus = [];
    s.categoryOptions = [];
    s.loading = false;
    s.error = null;
    s.isRerunFromResult = false;
    s.lockedSegmentTypes = [];
    s.lockedValuationMenus = [];
    s.draftParentRevision = 0;
    s.draftCategoryIds = Array(SLOT_COUNT).fill(null);
    s.skippedSegmentStep = false;
    s.analysisReport = null;
    clearRerunProjectIdSession();
  },
};

function slotArraysFromDetail(detail) {
  const items = (detail && detail.items) || [];
  const concepts = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const item = items.find(it => it.slot === i + 1);
    return item ? String(item.concept_text || '').trim() : '';
  });
  const conceptTitles = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const item = items.find(it => it.slot === i + 1);
    return item && item.concept_title != null ? String(item.concept_title) : '';
  });
  const categoryIds = Array.from({ length: SLOT_COUNT }, (_, i) => {
    const item = items.find(it => it.slot === i + 1);
    return item && item.category_id != null ? item.category_id : null;
  });
  return { concepts, conceptTitles, categoryIds };
}

function mergePreviewDetailWithPreviousCategories(previousDetail, previewPayload, draftCategoryIds) {
  const prevItems = (previousDetail && previousDetail.items) || [];
  const prevBySlot = {};
  prevItems.forEach((it) => {
    if (it && it.slot != null) {
      prevBySlot[Number(it.slot)] = it;
    }
  });
  const mergedItems = (previewPayload.items || []).map((it) => {
    const slot = Number(it.slot);
    const prev = prevBySlot[slot];
    const draftCid = draftCategoryIds && draftCategoryIds[slot - 1];
    const categoryId = (prev && prev.category_id != null)
      ? prev.category_id
      : (draftCid != null && draftCid !== '' ? draftCid : null);
    if (categoryId == null) {
      return it;
    }
    const label = (prev && prev.category_id === categoryId)
      ? prev.valuation_category_label
      : null;
    return {
      ...it,
      category_id: categoryId,
      valuation_category_label: label || null,
    };
  });
  return {
    ...(previousDetail || {}),
    ...previewPayload,
    items: mergedItems,
  };
}

const actions = {
  async conceptValuationCreate({ commit, state }, options = {}) {
    commit('CV_SET_LOADING', true);
    commit('CV_SET_ERROR', null);
    const { concepts, conceptTitles, categoryIds } = slotArraysFromDetail(state.detail);
    const draftMenus = (state.draftEvaluationMenus || []).map(Number);
    const routeQuery = options.routeQuery || null;
    const reuseProjectId = resolveReuseProjectId(state, routeQuery);
    const isRerunSession = isConceptValuationRerunActive(state, routeQuery);
    const shouldReuseProject = isRerunSession && reuseProjectId != null;
    const segmentTypes = shouldReuseProject
      ? newSegmentTypesForRerun(state.detail, draftMenus, state.draftSegmentTypes)
      : (state.draftSegmentTypes || []);
    const payload = {
      project_name: state.draftProjectName,
      target_country: state.draftTargetCountry,
      is_avg_segment: state.draftIsAvgSegment,
      segment_types: segmentTypes,
      parent_project_id: shouldReuseProject ? null : state.draftParentProjectId,
      valuation_menus: draftMenus,
      concepts,
      concept_titles: conceptTitles,
      category_ids: categoryIds,
    };
    if (shouldReuseProject) {
      payload.project_id = reuseProjectId;
    }
    if (options.processCreatedAt != null) {
      payload.process_created_at = options.processCreatedAt;
    }
    const { status, data } = await conceptEvaluationCreate(payload);
    commit('CV_SET_LOADING', false);
    if (status === 201) {
      commit('CV_SET_PROJECT_ID', data.project.id);
      commit('CV_SET_DETAIL', data);
      commit('CV_SET_ANALYSIS_REPORT', null);
      if (!shouldReuseProject) {
        clearRerunProjectIdSession();
      }
      return { ok: true, data };
    }
    commit('CV_SET_ERROR', data);
    return { ok: false, data };
  },
  async conceptValuationFetchDetail({ commit, state, rootState }, projectId) {
    const pid = projectId ?? state.projectId ?? state.detail?.project?.id;
    if (pid == null || pid === '') {
      return { ok: false, data: null };
    }
    commit('CV_SET_LOADING', true);
    const { status, data } = await conceptEvaluationDetail(pid);
    commit('CV_SET_LOADING', false);
    if (status === 200) {
      commit('CV_SET_DETAIL', data);
      commit('CV_SET_PROJECT_ID', data.project.id);
      syncRootTargetCountryFromProject(commit, data.project);
      try {
        await ensureRootPlanAdhoc(commit, rootState);
      } catch (_err) {
        // Adhoc plan price is optional; do not fail detail after 200.
      }
      return { ok: true, data };
    }
    return { ok: false, data };
  },
  async conceptValuationFetchCategories({ commit }) {
    const { status, data } = await conceptEvaluationCategoryList();
    if (status === 200) {
      commit('CV_SET_CATEGORY_OPTIONS', data);
      return { ok: true, data };
    }
    return { ok: false, data };
  },
  async conceptValuationGenerateConceptTitles({ commit, state }, concepts) {
    commit('CV_SET_LOADING', true);
    const { status, data } = await conceptEvaluationGenerateConceptTitles(concepts);
    commit('CV_SET_LOADING', false);
    if (status === 200) {
      const merged = mergePreviewDetailWithPreviousCategories(
        state.detail,
        data,
        state.draftCategoryIds,
      );
      commit('CV_SET_DETAIL', merged);
      return { ok: true, data: merged };
    }
    return { ok: false, data };
  },
  conceptValuationApplyCategories({ commit, state }, categoryIds) {
    const detail = state.detail;
    if (!detail || !detail.items) {
      return;
    }
    const labelById = {};
    (state.categoryOptions || []).forEach((opt) => {
      labelById[opt.id] = opt.name;
    });
    const items = detail.items.map((it) => {
      const cid = categoryIds[it.slot - 1];
      return {
        ...it,
        category_id: cid != null ? cid : null,
        valuation_category_label: cid != null ? labelById[cid] : null,
      };
    });
    commit('CV_SET_DETAIL', { ...detail, items });
    commit('CV_SET_DRAFT_CATEGORY_IDS', categoryIds);
  },
  conceptValuationSetDraft({ commit, state }, payload) {
    const hasParentInPayload = payload.parentProjectId !== undefined;
    const nextParentId = hasParentInPayload ? payload.parentProjectId : state.draftParentProjectId;
    const prevParentId = state.draftParentProjectId;
    const parentChanged = hasParentInPayload
      && Number(prevParentId || 0) !== Number(nextParentId || 0);

    // Only wipe wizard preview/categories when parent actually changes.
    // NewProject re-submit with the same parentProjectId must keep draftCategoryIds
    // so SelectCategory can restore previous selections after back → register → next.
    if (parentChanged) {
      commit('CV_CLEAR_RERUN_SESSION');
      clearRerunProjectIdSession();
      commit('CV_SET_PROJECT_ID', null);
      commit('CV_SET_DETAIL', null);
      commit('CV_SET_DRAFT_CATEGORY_IDS', []);
      commit('CV_SET_ANALYSIS_REPORT', null);
      commit('CV_SET_DRAFT', {
        segmentTypes: [],
        valuationMenus: [],
        skippedSegmentStep: false,
      });
      commit('CV_SET_CATEGORY_OPTIONS', []);
      commit('CV_BUMP_PARENT_REVISION');
    }

    commit('CV_SET_DRAFT', payload);
    if (payload.targetCountry !== undefined) {
      syncRootTargetCountryFromProject(commit, { target_country: payload.targetCountry });
    }
  },
  conceptValuationMarkRerunFromResult({ commit }, payload) {
    commit('CV_SET_RERUN_FROM_RESULT', payload);
    if (payload && payload.projectId != null && payload.projectId !== '') {
      writeRerunProjectIdToSession(payload.projectId);
    }
  },
  conceptValuationClearRerunSession({ commit }) {
    commit('CV_CLEAR_RERUN_SESSION');
    clearRerunProjectIdSession();
  },
  async conceptValuationCreateAnalysisReport({ commit }, { projectId, itemId }) {
    commit('CV_SET_LOADING', true);
    commit('CV_SET_ERROR', null);
    commit('CV_SET_ANALYSIS_REPORT', null);
    try {
      const response = await conceptEvaluationCreateAnalysisReport(projectId, itemId);
      if (!response) {
        return { ok: false, data: null };
      }
      const { status, data } = response;
      if (status === 201) {
        commit('CV_SET_ANALYSIS_REPORT', data);
        return { ok: true, data };
      }
      commit('CV_SET_ERROR', data);
      return { ok: false, data };
    } finally {
      commit('CV_SET_LOADING', false);
    }
  },
  async conceptValuationFetchAnalysisReport({ commit }, { projectId, reportId }) {
    commit('CV_SET_LOADING', true);
    try {
      const response = await conceptEvaluationAnalysisReportDetail(projectId, reportId);
      if (!response) {
        return { ok: false, data: null };
      }
      const { status, data } = response;
      if (status === 200) {
        commit('CV_SET_ANALYSIS_REPORT', data);
        return { ok: true, data };
      }
      return { ok: false, data };
    } finally {
      commit('CV_SET_LOADING', false);
    }
  },
  conceptValuationReset({ commit }) {
    commit('CV_RESET');
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
